import { Europafacts } from "@/services/api";
import { useDatabaseStore } from "@/stores/database";
import { useDataflowStore } from "@/stores/dataflow";
import type { UseAutoSaveOptions } from "@/types/hooks";
import { logger } from "@/utils/debug";
import { useCallback, useEffect, useRef } from "react";

/**
 * Hook to automatically save the dataflow
 * @param options - The options for the hook
 * @returns The triggerAutoSave and performSave functions
 */
export function useAutoSave({
  dataflowId,
  projectId,
  debounceMs = 1500,
  onSaveStart,
  onSaveComplete,
  onSaveError,
}: UseAutoSaveOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const { setSaving, updateDataflow, addDataflow } = useDataflowStore();
  const updateDatabaseDataflow = useDatabaseStore(
    (state) => state.updateDataflow
  );

  const performSave = useCallback(async () => {
    const currentDataflowId =
      dataflowId || useDataflowStore.getState().activeDataflowId;
    if (!currentDataflowId) {
      return;
    }

    const freshDataflowData =
      useDataflowStore.getState().dataflows[currentDataflowId];
    if (!freshDataflowData || isSavingRef.current) {
      return;
    }

    try {
      isSavingRef.current = true;
      if (onSaveStart) onSaveStart();
      setSaving(currentDataflowId, true);

      let savedDataflow;

      if (currentDataflowId) {
        savedDataflow = await Europafacts.updateDataflow(
          currentDataflowId,
          freshDataflowData
        );

        updateDataflow(currentDataflowId, {
          dataflow_id: savedDataflow.dataflow_id,
          datetime_updated: savedDataflow.datetime_updated,
        });

        await updateDatabaseDataflow(currentDataflowId, {
          dataflow_name: savedDataflow.dataflow_name,
          dataflow_description: savedDataflow.dataflow_description,
          flow_definition_json: savedDataflow.flow_definition_json,
          status: savedDataflow.status,
        });
      } else {
        const name =
          freshDataflowData.dataflow_name ||
          `Dataflow ${new Date().toISOString().split("T")[0]}`;
        const description =
          freshDataflowData.dataflow_description || "User dataflow";

        savedDataflow = await Europafacts.saveDataflow(
          freshDataflowData,
          name,
          description,
          projectId
        );

        addDataflow(savedDataflow.dataflow_id, {
          ...freshDataflowData,
          dataflow_id: savedDataflow.dataflow_id,
          dataflow_name: name,
          dataflow_description: description,
          project_id: projectId,
          datetime_created: savedDataflow.datetime_created,
          datetime_updated: savedDataflow.datetime_updated,
        });
      }

      if (onSaveComplete) onSaveComplete(savedDataflow.dataflow_id);
    } catch (error) {
      logger.error("Auto-save failed:", error);
      if (onSaveError)
        onSaveError(
          error instanceof Error ? error : new Error("Auto-save failed")
        );
    } finally {
      isSavingRef.current = false;

      if (currentDataflowId) {
        setSaving(currentDataflowId, false);
      }
    }
  }, [
    dataflowId,
    projectId,
    onSaveStart,
    onSaveComplete,
    onSaveError,
    setSaving,
    updateDataflow,
    addDataflow,
    updateDatabaseDataflow,
  ]);

  const triggerAutoSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performSave();
    }, debounceMs);
  }, [performSave, debounceMs]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    triggerAutoSave,
    performSave,
  };
}
